import {useEffect, useId, useMemo, useRef, useState} from "react";
import type {KeyboardEvent, ReactNode} from "react";
import {CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, UsersIcon} from "@heroicons/react/24/outline";
import {cn} from "../../../lib/utils.ts";
import {isMacOS} from "../../../lib/platform.ts";
import {buildClientSections, getClientInitials} from "./utils";
import type {SwitcherClient} from "./utils";

interface ClientSwitcherProps {
    clients: SwitcherClient[];
    selectedClientId: string | null;
    recentClientIds: string[];
    onSelect: (clientId: string | null) => void;
}

interface OptionEntry {
    key: string;
    clientId: string | null;
    label: string;
}

const IS_MAC = isMacOS();

function Chip({children}: { children: ReactNode }) {
    return (
        <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-superliteprimary text-xs font-semibold text-primary"
        >
            {children}
        </span>
    );
}

export function ClientSwitcher({clients, selectedClientId, recentClientIds, onSelect}: ClientSwitcherProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();

    const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;
    const sections = useMemo(
        () => buildClientSections(clients, recentClientIds, query),
        [clients, recentClientIds, query],
    );

    const entries = useMemo<OptionEntry[]>(() => {
        const toEntry = (client: SwitcherClient): OptionEntry => ({key: client.id, clientId: client.id, label: client.name});
        const allClients: OptionEntry[] = query.trim() ? [] : [{key: 'all', clientId: null, label: 'Alle klanten'}];
        return [...allClients, ...sections.recent.map(toEntry), ...sections.rest.map(toEntry)];
    }, [sections, query]);

    const optionId = (entry: OptionEntry) => `${listboxId}-${entry.key}`;

    const close = (restoreFocus: boolean) => {
        setIsOpen(false);
        setQuery('');
        if (restoreFocus) triggerRef.current?.focus();
    };

    const open = () => {
        const selectedIndex = entries.findIndex((entry) => entry.clientId === selectedClientId);
        setActiveIndex(Math.max(selectedIndex, 0));
        setIsOpen(true);
    };

    const select = (entry: OptionEntry) => {
        onSelect(entry.clientId);
        close(true);
    };

    useEffect(() => {
        function onKeyDown(event: globalThis.KeyboardEvent) {
            const hasModifier = IS_MAC ? event.metaKey : event.ctrlKey;
            if (!hasModifier || event.key.toLowerCase() !== 'j') return;
            event.preventDefault();
            if (isOpen) {
                close(true);
            } else {
                open();
            }
        }

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    });

    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        function onMouseDown(event: MouseEvent) {
            if (!containerRef.current?.contains(event.target as Node)) close(false);
        }

        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [isOpen]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, entries.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const entry = entries[activeIndex];
            if (entry) select(entry);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
        }
    };

    const activeEntry = entries[activeIndex];

    const renderOption = (entry: OptionEntry, index: number) => {
        const isSelected = entry.clientId === selectedClientId;
        return (
            <li
                key={entry.key}
                id={optionId(entry)}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(entry)}
                className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm text-gray-900",
                    index === activeIndex && "bg-gray-50",
                )}
            >
                <Chip>{entry.clientId ? getClientInitials(entry.label) : <UsersIcon className="h-4 w-4"/>}</Chip>
                <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                {isSelected && <CheckIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true"/>}
            </li>
        );
    };

    const offset = entries.length > 0 && entries[0].clientId === null ? 1 : 0;
    const recentOffset = offset;
    const restOffset = offset + sections.recent.length;

    return (
        <div ref={containerRef} className="relative">
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={selectedClient ? `Klant: ${selectedClient.name}` : 'Klant: Alle klanten'}
                title="Wissel van klant"
                onClick={() => (isOpen ? close(false) : open())}
                className="flex h-10 max-w-full items-center gap-2 rounded-lg border border-gray-200 bg-white pl-1.5 pr-2 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
            >
                <Chip>{selectedClient ? getClientInitials(selectedClient.name) : <UsersIcon className="h-4 w-4"/>}</Chip>
                <span className="hidden max-w-[220px] truncate md:inline">{selectedClient?.name ?? 'Alle klanten'}</span>
                <ChevronUpDownIcon className="hidden h-4 w-4 shrink-0 text-gray-400 md:block" aria-hidden="true"/>
            </button>

            {isOpen && (
                <div className="absolute right-0 z-50 mt-2 w-[360px] max-w-[calc(100vw-2rem)] rounded-xl bg-white shadow-lg ring-1 ring-black/5">
                    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5">
                        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true"/>
                        <input
                            ref={inputRef}
                            type="text"
                            role="combobox"
                            aria-expanded="true"
                            aria-controls={listboxId}
                            aria-activedescendant={activeEntry ? optionId(activeEntry) : undefined}
                            aria-label="Zoek klant"
                            autoComplete="off"
                            placeholder="Zoek klant op naam..."
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={onInputKeyDown}
                            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                        />
                        <kbd className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">Esc</kbd>
                    </div>

                    <ul id={listboxId} role="listbox" aria-label="Klanten" className="max-h-80 overflow-y-auto p-1.5">
                        {offset === 1 && renderOption(entries[0], 0)}
                        {sections.recent.length > 0 && (
                            <li role="presentation">
                                <p className="px-2 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Recent</p>
                                <ul role="group" aria-label="Recent">
                                    {sections.recent.map((_, i) => renderOption(entries[recentOffset + i], recentOffset + i))}
                                </ul>
                            </li>
                        )}
                        {sections.rest.length > 0 && (
                            <li role="presentation">
                                <p className="px-2 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Alle klanten</p>
                                <ul role="group" aria-label="Alle klanten">
                                    {sections.rest.map((_, i) => renderOption(entries[restOffset + i], restOffset + i))}
                                </ul>
                            </li>
                        )}
                        {sections.recent.length === 0 && sections.rest.length === 0 && (
                            <li role="presentation" className="px-2 py-6 text-center text-sm text-gray-500">Geen klanten gevonden</li>
                        )}
                    </ul>

                    <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                        <span>↑↓ kiezen ↵ openen</span>
                        <span>Wisselen {IS_MAC ? '⌘J' : 'Ctrl J'}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
